import React, { FC } from "react";
import styled from "styled-components/macro";
import { CSSObject as SerializeCSSObject } from "@emotion/serialize";
import BaseSelect, {
  ControlProps,
  ContainerProps,
  GroupTypeBase,
  OptionProps,
  FocusEventHandler
} from "react-select";
import { defaultTheme } from "../../theme/defaultTheme";
import { ReactComponent as arrowIcon } from "../../assets/arrow.svg";
import { Label } from "./Label";
import { t } from "../../utils/intl";
import { InvalidMessage } from "./styled";

export interface CSSObject {
  [key: string]: string | number | undefined;
}
interface ISelectProps {
  isInvalid?: boolean;
  value?: any;
  id?: string;
  onChange: (value: any) => void;
  options?: any;
  rowNumber?: number;
  onBlur?: FocusEventHandler;
  labelTrKey?: string;
  isInvalidMessage?: string;
  className?: string;
  customStylesExtension?: any;
}

const MainSelect = ({
  labelTrKey,
  options,
  isInvalid,
  onChange,
  isInvalidMessage,
  value,
  id,
  onBlur,
  rowNumber,
  className,
  customStylesExtension
}: ISelectProps) => {
  return (
    <MainSelectContainer className={className}>
      {labelTrKey && <Label rowNumber={rowNumber} labelTrKey={labelTrKey} />}
      <BaseSelect
        isSearchable={false}
        id={id}
        components={{ DropdownIndicator }}
        onBlur={onBlur}
        value={value}
        onChange={selectedOption => onChange(selectedOption)}
        options={options || []}
        placeholder={t(`form.choose`)}
        isInvalid={isInvalid}
        styles={customStyles(isInvalid, customStylesExtension)}
        blurInputOnSelect={false}
      />
      {isInvalid && isInvalidMessage && <InvalidMessage>{t(isInvalidMessage)}</InvalidMessage>}
    </MainSelectContainer>
  );
};
interface IDropdownIndicatorProps {
  menuIsOpen?: boolean;
}
const DropdownIndicator: FC<{ selectProps: IDropdownIndicatorProps }> = ({ selectProps }) => {
  return <>{selectProps.menuIsOpen ? <BottomArrowDark /> : <TopArrowDark />}</>;
};

export default MainSelect;

const TopArrowDark = styled(arrowIcon)`
  margin: 0 auto;
  & g {
    fill: ${({ theme }) => theme.newColors.black};
  }
  @media print {
    visibility: hidden;
  }
`;

const BottomArrowDark = styled(arrowIcon)`
  margin: 0 auto;
  transform: rotate(180deg);
  & g {
    fill: ${({ theme }) => theme.newColors.black};
  }
  @media print {
    visibility: hidden;
  }
`;

const MainSelectContainer = styled.div`
  page-break-inside: avoid;
  margin: 56px 0;
`;

const customStyles = (isInvalid?: boolean, customStylesExtension?: any) => {
  return {
    container: (
      provided: CSSObject,
      state: ContainerProps<any, any, GroupTypeBase<any>>
    ): CSSObject => ({
      ...provided,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      pointerEvents: "initial",
      color: defaultTheme.newColors.gray100,
      fontSize: "18px",
      linHeight: "20px",
      fontWeight: 600,

      "::-moz-scrollbar-button, ::-webkit-scrollbar-button": {
        width: "0px"
      },

      ...customStylesExtension?.container
    }),
    control: (provided: CSSObject, state: ControlProps<any, any, GroupTypeBase<any>>) => ({
      width: "100%",
      background: state.isDisabled ? defaultTheme.newColors.gray10 : defaultTheme.newColors.white,
      display: "flex",
      border: 0,
      justifyContent: "space-evenly",
      padding: "16px 12px",

      ...(() => {
        if (state.isFocused)
          return { boxShadow: `inset 0px 0px 0px 2px ${defaultTheme.newColors.blue}` };
        if (isInvalid)
          return { boxShadow: `inset 0px 0px 0px 2px ${defaultTheme.newColors.error}` };
        return { boxShadow: `inset 0px 0px 0px 1px ${defaultTheme.newColors.gray30}` };
      })(),

      ...(!state.isFocused && !state.isDisabled
        ? {
            "&:hover": {
              boxShadow: `inset 0px 0px 0px 2px ${defaultTheme.colors.blue}`
            }
          }
        : {}),

      ...customStylesExtension?.control
    }),
    valueContainer: (provided: CSSObject) => ({
      ...provided,
      justifyContent: "left",
      paddingRight: 0,
      padding: 0,
      display: "-webkit-box",
      webkitBoxPack: "start",
      ...customStylesExtension?.valueContainer
    }),
    singleValue: (provided: CSSObject) => ({
      ...provided,
      margin: "unset",
      ...customStylesExtension?.singleValue
    }),
    input: (provided: SerializeCSSObject) => ({
      ...provided,
      padding: "unset",
      margin: "unset",
      ...customStylesExtension?.input
    }),
    indicatorsContainer: (provided: CSSObject) => ({
      ...provided,
      padding: "unset",
      height: "20px",
      width: "20px",
      alignSelf: "center",
      ...customStylesExtension?.indicatorsContainer
    }),
    indicatorSeparator: () => ({ ...customStylesExtension?.indicatorSeparator }),
    menu: (provided: CSSObject) => ({
      ...provided,
      marginTop: "-2px",
      boxShadow: defaultTheme.shadows.elevation5,
      background: defaultTheme.newColors.white,
      zIndex: 12,
      border: `2px solid ${defaultTheme.newColors.blue}`,
      borderRadius: "unset",
      ...customStylesExtension?.menu
    }),
    option: (provided: CSSObject, state: OptionProps<any, any, GroupTypeBase<any>>) => ({
      padding: "12px",
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
      cursor: "pointer",
      textAlign: "left",
      backgroundColor: defaultTheme.newColors.white,
      borderTop: `solid 1px ${defaultTheme.newColors.gray40}`,

      "&:first-of-type": {
        borderTop: "none"
      },

      "&:hover": {
        backgroundColor: defaultTheme.newColors.primary10,
        borderColor: defaultTheme.newColors.primary80,

        "& + div": {
          borderColor: defaultTheme.newColors.primary80
        }
      },

      ...(state.isSelected
        ? {
            "&&&": {
              color: defaultTheme.newColors.white,
              backgroundColor: defaultTheme.newColors.primary100,
              borderTop: "none"
            },

            ":hover": {
              color: defaultTheme.newColors.white,
              backgroundColor: defaultTheme.newColors.primary100
            }
          }
        : {}),

      ...customStylesExtension?.option
    }),
    menuList: (provided: CSSObject) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,

      "::-webkit-scrollbar": {
        width: "8px",
        height: "0px"
      },

      "::-webkit-scrollbar-track": {
        backgroundColor: defaultTheme.newColors.gray10
      },

      "::-webkit-scrollbar-thumb": {
        backgroundColor: defaultTheme.newColors.gray40
      },

      ...customStylesExtension?.menuList
    })
  };
};
